import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import styles from './share-in-team-toggle-bar.module.scss';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {noop} from '@Utils/general.util';
import type {ShareInTeamToggleBarProps} from './share-in-team-toggle-bar.types';
import {ShareInTeamBarState} from './share-in-team-toggle-bar.types';
import {MailingListsTestId} from '@/test-ids/mailing-lists-test-id';

export function ShareInTeamToggleBar({
  state = ShareInTeamBarState.INACTIVE,
  onStateUpdate = noop,
  isCurrentlyLoading = false,
  customClasses = '',
  toggleSize = ToggleSwitchV2Size.SMALL,
  shareInTeamIcon = undefined,
}: ShareInTeamToggleBarProps): ReactElement<any> {
  const [isLoading, setIsLoading] = useState(isCurrentlyLoading);

  useEffect(() => {
    setIsLoading(isCurrentlyLoading);
  }, [isCurrentlyLoading]);

  const isInactive = (): boolean => {
    return state === ShareInTeamBarState.INACTIVE;
  };

  const isActive = (): boolean => {
    return state === ShareInTeamBarState.ACTIVE;
  };

  const isUpselling = (): boolean => {
    return state === ShareInTeamBarState.UPSELLING;
  };

  const getBarClassName = (): string => {
    if (isActive()) {
      return styles.active;
    }

    if (isUpselling()) {
      return styles.upselling;
    }

    return styles.inactive;
  };

  const getTeamFlatIconColor = (): FlatIconColor => {
    if (isActive()) {
      return FlatIconColor.PRIMARY;
    }

    if (isUpselling()) {
      return FlatIconColor.PREMIUM;
    }

    return FlatIconColor.NEUTRAL;
  };

  const onToggleSwitched = (): void => {
    if (isInactive()) {
      onStateUpdate(ShareInTeamBarState.ACTIVE);
    } else if (isUpselling()) {
      setIsLoading(true);
      onStateUpdate(ShareInTeamBarState.UPSELLING);
      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_TEAMS as string, noop, true);
      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } else {
      onStateUpdate(ShareInTeamBarState.INACTIVE);
    }
  };

  const getShareInTeamIcon = (): ReactElement<any> => {
    if (!shareInTeamIcon) {
      return getDefaultShareInTeamIcon();
    }

    return shareInTeamIcon;
  };

  const getDefaultShareInTeamIcon = (): ReactElement<any> => {
    return (
      <Icon
        icon="icon-team"
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        type={IconType.FLAT}
        flatIconType={{color: getTeamFlatIconColor(), type: FlatIconColorType.DARK}}
      />
    );
  };

  return (
    <div className={`radius-12 spacing-p-t-2 spacing-p-b-2 spacing-p-l-2 spacing-p-r-3 flex-row-justify-between ${styles.bar} ${getBarClassName()} ${customClasses}`}>
      <div className="flex-row-align-center">
        {getShareInTeamIcon()}
        <Text val={window.i18next.t('pmwjs_share_in_team')} size={TextSize.XSMALL} className={`spacing-m-l-2 spacing-m-r-2 content-body ${styles.textNoWrap}`} />
        {isUpselling() ? <Icon icon="icon-crown" size={IconSize.SIZE_ICON_16} className="-premium-color -premium-color-hover spacing-m-l-2" /> : null}
      </div>
      <ToggleSwitch isChecked={isActive()} onChange={onToggleSwitched} size={toggleSize} className="flex-row-align-center" isLoading={isLoading} data-testid={MailingListsTestId.SHARE_IN_TEAM_TOGGLE} />
    </div>
  );
}
